// "Check in" on the circle page: a preset or free-text message plus one
// browser geolocation reading, sent to this circle. The reading is stored
// only on the check-in, never as a location fix.
import type { Schemas } from "@10twenty/api";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { CHECK_IN_PRESETS, checkInMessage, MESSAGE_MAX, messageLength, pointFromPosition } from "../checkins/checkIns";
import { currentPosition, LocationUnavailable } from "../checkins/geolocation";

const APPROXIMATE =
  "You share only an approximate area with this circle, and a check-in shows where you are. " +
  "Share your exact location again to check in.";

function checkInError(error: unknown): string {
  if (error instanceof LocationUnavailable) return error.message;
  if (error instanceof ApiError && error.code === "sharing_paused")
    return "You've paused sharing with this circle. Resume sharing to check in.";
  if (error instanceof ApiError && error.code === "approximate_sharing") return APPROXIMATE;
  return errorMessage(error, {
    400: "Your location reading was too old. Try again.",
    404: "You're no longer a member of this circle.",
    429: "You've checked in a lot in the last hour. Try again later.",
  });
}

export function CheckInCard({
  circle,
  paused,
  approximate,
}: {
  circle: Schemas["Circle"];
  paused: boolean;
  /** A sharing bubble covers this circle: the server refuses check-ins (403 approximate_sharing). */
  approximate: boolean;
}) {
  const [text, setText] = useState("");
  const [sent, setSent] = useState<Schemas["CheckIn"] | null>(null);
  const checkIn = useMutation({
    mutationFn: async (message: string | null) => {
      const point = pointFromPosition(await currentPosition());
      return unwrap(
        await api.POST("/circles/{circleId}/check-ins", {
          params: { path: { circleId: circle.id } },
          body: { message, point },
        }),
      );
    },
    onSuccess: (created) => {
      setSent(created);
      setText("");
    },
  });
  const tooLong = messageLength(text) > MESSAGE_MAX;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (tooLong || paused || approximate) return;
    setSent(null);
    checkIn.mutate(checkInMessage(text));
  }

  return (
    <section className="card stack">
      <h2>Check in</h2>
      <p className="muted">
        Let {circle.name} know where you are. Your browser reads your location once; it's sent with this check-in only.
      </p>
      {paused ? (
        <p className="muted">You've paused sharing with this circle. Resume sharing to check in.</p>
      ) : approximate ? (
        <p className="muted">{APPROXIMATE}</p>
      ) : (
        <form className="stack" onSubmit={submit}>
          <div className="check-in-presets" role="group" aria-label="Message">
            {CHECK_IN_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`secondary${text === preset ? " selected" : ""}`}
                aria-pressed={text === preset}
                disabled={checkIn.isPending}
                onClick={() => setText(text === preset ? "" : preset)}
              >
                {preset}
              </button>
            ))}
          </div>
          <label>
            Message (optional)
            <input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="I'm here"
              readOnly={checkIn.isPending}
              aria-invalid={tooLong}
            />
          </label>
          {tooLong && <p className="error">Keep it to {MESSAGE_MAX} characters.</p>}
          <div role="alert">{checkIn.error && <p className="error">{checkInError(checkIn.error)}</p>}</div>
          {sent && (
            <p role="status">
              Checked in to {circle.name} at{" "}
              {new Date(sent.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.
            </p>
          )}
          <button type="submit" disabled={checkIn.isPending || tooLong}>
            {checkIn.isPending ? "Checking in…" : "Check in"}
          </button>
        </form>
      )}
    </section>
  );
}
